import { useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { ObjectId } from 'bson';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { getCompanyInfo } from '../../../api/company';
import { addressSchemas } from '../../../constants/addressSchemas';
import { useUpdateCompany } from '../../../hooks/useCompany';
import { useThailandAddress } from '../../../hooks/useThailandAddress';
import { useDialogStore } from '../../../stores/dialogStore';
import { useUserStore } from '../../../stores/userStore';
import AddressInput from './AddressInput';
import AddressSelect from './AddressSelect';
import AddressTextArea from './AddressTextArea';

const AddressForm = ({ mode = 'ADD' }) => {
  const { addressId } = useParams();
  const navigate = useNavigate();

  const {
    provinces,
    filteredDistricts,
    filteredSubDistricts,
    selectProvince,
    selectDistrict,
    getZipcodeBySubDistrictName,
    hydrateAddressSelection,
  } = useThailandAddress();

  const openDialog = useDialogStore((state) => state.openDialog);

  const companyInfo = useUserStore((state) => state.companyInfo);
  const setCompanyInfo = useUserStore((state) => state.setCompanyInfo);

  const { mutateAsync: updateCompany } = useUpdateCompany();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullname: '',
      phone: '',
      address: '',
      alley: '',
      street: '',
      province: '',
      district: '',
      subdistrict: '',
      zipcode: '',
    },
    validationSchema: addressSchemas,
    onSubmit: async (values) => {
      if (mode === 'EDIT') {
        // Case: Edit address
        try {
          // Update address
          const newAddressList = companyInfo.address.map((item) => {
            if (item._id === addressId) {
              return {
                ...item,
                name: values.fullname,
                phonenumber: values.phone,
                detail: values.address,
                alley: values.alley,
                street_name: values.street,
                province: values.province,
                district: values.district,
                subdistrict: values.subdistrict,
                zip_code: values.zipcode.toString(),
              };
            }
            return item;
          });
          // Update company address
          const response = await updateCompany({
            params: { id: companyInfo._id },
            dto: { address: newAddressList },
          });
          // Success
          if (response.Code === '20100') {
            setCompanyInfo(response.Data);
            openDialog({
              variant: 'success',
              title: 'สำเร็จ',
              message: 'แก้ไขที่อยู่สำเร็จ',
              onCloseCallback: () => navigate('/address'),
            });
          }
        } catch (err) {
          console.error(err);
          openDialog({
            variant: 'error',
            title: 'เกิดข้อผิดพลาด',
            message: err.response.data.Data[0].message ?? 'แก้ไขที่อยู่ไม่สำเร็จ',
          });
        }
      } else {
        // Case: Add new address
        try {
          // New address
          const newAddress = {
            _id: new ObjectId(),
            name: values.fullname,
            phonenumber: values.phone,
            detail: values.address,
            alley: values.alley,
            street_name: values.street,
            province: values.province,
            district: values.district,
            subdistrict: values.subdistrict,
            zip_code: values.zipcode.toString(),
            default: companyInfo.address.length === 0,
          };
          const updateAddress = companyInfo.address.map((item) => ({
            ...item,
            alley: item.alley ?? '-',
            street_name: item.street_name ?? '-',
          }));
          // New address list
          const addressList = [...updateAddress, newAddress];
          // Update company address
          const response = await updateCompany({
            params: { id: companyInfo._id },
            dto: { address: addressList },
          });
          // Success
          if (response.Code === '20100') {
            setCompanyInfo(response.Data);
            openDialog({
              variant: 'success',
              title: 'สำเร็จ',
              message: 'เพิ่มที่อยู่ใหม่สำเร็จ',
              onCloseCallback: () => navigate('/address'),
            });
          }
        } catch (err) {
          console.error(err);
          openDialog({
            variant: 'error',
            title: 'เกิดข้อผิดพลาด',
            message: err.response.data.Data[0].message ?? 'แก้ไขที่อยู่ไม่สำเร็จ',
          });
        }
      }
    },
  });

  const onProvinceChange = useCallback(
    (value) => {
      selectProvince(value);
      formik.setFieldValue('province', value);
      formik.setFieldValue('district', '');
      formik.setFieldValue('subdistrict', '');
      formik.setFieldValue('zipcode', '');
    },
    // eslint-disable-next-line
    [selectProvince]
  );

  const onDistrictChange = useCallback(
    (value, provinceName = formik.values.province) => {
      selectDistrict(value, provinceName);
      formik.setFieldValue('district', value);
      formik.setFieldValue('subdistrict', '');
      formik.setFieldValue('zipcode', '');
    },
    // eslint-disable-next-line
    [selectDistrict, formik.values.province]
  );

  const onSubDistrictChange = useCallback((value) => {
    formik.setFieldValue('subdistrict', value);
    formik.setFieldValue('zipcode', getZipcodeBySubDistrictName(value));
    // eslint-disable-next-line
  }, [getZipcodeBySubDistrictName]);

  useEffect(() => {
    if (!companyInfo?._id || mode === 'ADD') return;

    const fetchCompanyInfo = async () => {
      try {
        const response = await getCompanyInfo({ id: companyInfo._id });

        if (response.Code === '20000') {
          const address = response.Data.data[0].address.find((item) => item._id === addressId);
          const addressSelection = hydrateAddressSelection({
            province: address.province,
            district: address.district,
            subdistrict: address.subdistrict,
          });

          formik.setValues({
            fullname: address.name,
            phone: address.phonenumber,
            address: address.detail,
            alley: address.alley,
            street: address.street_name,
            province: address.province,
            district: address.district,
            subdistrict: address.subdistrict,
            zipcode: addressSelection.zipcode || address.zip_code,
          });
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchCompanyInfo();
    // eslint-disable-next-line
  }, [companyInfo?._id, addressId, mode]);

  return (
    <Box
      component={'form'}
      onSubmit={formik.handleSubmit}
      sx={{
        padding: '20px',
        borderRadius: '8px',
        backgroundColor: 'white',
        marginBottom: '20px',
      }}
    >
      <Box
        sx={(theme) => ({
          [theme.breakpoints.up('sm')]: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: '20px',
          },
        })}
      >
        {/* Name */}
        <Box mb={2}>
          <FormLabel>ชื่อ - นามสกุล</FormLabel>
          <AddressInput
            id="fullname"
            name="fullname"
            value={formik.values.fullname || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.fullname && formik.errors.fullname}
          />
          {formik.touched.fullname && formik.errors.fullname && (
            <Typography sx={{ fontSize: '1.25rem', padding: '5px 0', color: '#ED1C24' }}>
              {formik.errors.fullname}
            </Typography>
          )}
        </Box>

        {/* Phone */}
        <Box mb={2}>
          <FormLabel>เบอร์โทรศัพท์</FormLabel>
          <AddressInput
            id="phone"
            name="phone"
            value={formik.values.phone || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            inputProps={{
              maxLength: '10',
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            error={formik.touched.phone && formik.errors.phone}
          />
          {formik.touched.phone && formik.errors.phone && (
            <Typography sx={{ fontSize: '1.25rem', padding: '5px 0', color: '#ED1C24' }}>
              {formik.errors.phone}
            </Typography>
          )}
        </Box>

        {/* Address */}
        <Box mb={2}>
          <FormLabel>บ้านเลขที่ / หมู่บ้าน</FormLabel>
          <AddressTextArea
            id="address"
            name="address"
            multiline
            minRows={3}
            value={formik.values.address || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address && formik.errors.address}
          />
          {formik.touched.address && formik.errors.address && (
            <Typography sx={{ fontSize: '1.25rem', padding: '5px 0', color: '#ED1C24' }}>
              {formik.errors.address}
            </Typography>
          )}
        </Box>

        {/* Alley */}
        <Box mb={2}>
          <FormLabel>ซอย</FormLabel>
          <AddressInput
            id="alley"
            name="alley"
            value={formik.values.alley || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.alley && formik.errors.alley}
          />
          {formik.touched.alley && formik.errors.alley && (
            <Typography sx={{ fontSize: '1.25rem', padding: '5px 0', color: '#ED1C24' }}>
              {formik.errors.alley}
            </Typography>
          )}
        </Box>

        {/* Street */}
        <Box mb={2}>
          <FormLabel>ถนน</FormLabel>
          <AddressInput
            id="street"
            name="street"
            value={formik.values.street}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.street && formik.errors.street}
          />
          {formik.touched.street && formik.errors.street && (
            <Typography sx={{ fontSize: '1.25rem', padding: '5px 0', color: '#ED1C24' }}>
              {formik.errors.street}
            </Typography>
          )}
        </Box>

        {/* Province */}
        <Box mb={2}>
          <FormLabel>จังหวัด</FormLabel>
          <AddressSelect
            id="province"
            name="province"
            value={formik.values.province || ''}
            onChange={(e) => onProvinceChange(e.target.value)}
            error={formik.touched.province && formik.errors.province}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <Typography sx={{ color: 'transparent' }}>ไม่พบจังหวัด</Typography>;
              }

              return selected;
            }}
          >
            {provinces.length > 0 ? (
              provinces.map((item) => (
                <MenuItem key={item.id} value={item.name_th}>
                  {item.name_th}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled value="">
                ไม่พบจังหวัด
              </MenuItem>
            )}
          </AddressSelect>
          {formik.touched.province && formik.errors.province && (
            <Typography sx={{ fontSize: '1.25rem', padding: '5px 0', color: '#ED1C24' }}>
              {formik.errors.province}
            </Typography>
          )}
        </Box>

        {/* District */}
        <Box mb={2}>
          <FormLabel>อำเภอ</FormLabel>
          <AddressSelect
            id="district"
            name="district"
            value={formik.values.district || ''}
            onChange={(e) => onDistrictChange(e.target.value)}
            error={formik.touched.district && formik.errors.district}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <Typography sx={{ color: 'transparent' }}>ไม่พบอำเภอ</Typography>;
              }

              return selected;
            }}
          >
            {filteredDistricts.length > 0 ? (
              filteredDistricts.map((item) => (
                <MenuItem key={item.id} value={item.name_th}>
                  {item.name_th}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled value="">
                ไม่พบอำเภอ
              </MenuItem>
            )}
          </AddressSelect>
          {formik.touched.district && formik.errors.district && (
            <Typography sx={{ fontSize: '1.25rem', padding: '5px 0', color: '#ED1C24' }}>
              {formik.errors.district}
            </Typography>
          )}
        </Box>

        {/* Subdistrict */}
        <Box mb={2}>
          <FormLabel>ตำบล</FormLabel>
          <AddressSelect
            id="tambon"
            name="tambon"
            value={formik.values.subdistrict || ''}
            onChange={(e) => onSubDistrictChange(e.target.value)}
            error={formik.touched.subdistrict && formik.errors.subdistrict}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <Typography sx={{ color: 'transparent' }}>ไม่พบตำบล</Typography>;
              }

              return selected;
            }}
          >
            {filteredSubDistricts.length > 0 ? (
              filteredSubDistricts.map((item) => (
                <MenuItem key={item.id} value={item.name_th}>
                  {item.name_th}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled value="">
                ไม่พบตำบล
              </MenuItem>
            )}
          </AddressSelect>
          {formik.touched.subdistrict && formik.errors.subdistrict && (
            <Typography sx={{ fontSize: '1.25rem', padding: '5px 0', color: '#ED1C24' }}>
              {formik.errors.subdistrict}
            </Typography>
          )}
        </Box>

        {/* Zipcode */}
        <Box mb={2}>
          <FormLabel>รหัสไปรษณีย์</FormLabel>
          <AddressInput
            id="zipcode"
            name="zipcode"
            value={formik.values.zipcode || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            inputProps={{
              maxLength: '5',
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            error={formik.touched.zipcode && formik.errors.zipcode}
          />
          {formik.touched.zipcode && formik.errors.zipcode && (
            <Typography sx={{ fontSize: '1.25rem', padding: '5px 0', color: '#ED1C24' }}>
              {formik.errors.zipcode}
            </Typography>
          )}
        </Box>

        {/* Confirm button */}
        <Box
          mt={5}
          mb={3}
          sx={(theme) => ({
            [theme.breakpoints.up('sm')]: {
              gridColumn: 'span 2',
              width: '250px',
              margin: '40px auto 24px',
            },
          })}
        >
          <Button
            type="submit"
            fullWidth
            sx={{
              maxWidth: '250px',
              height: '45px',
              fontSize: '1.5rem',
              fontWeight: '500',
              display: 'block',
              margin: '0 auto',
              padding: '2px 10px',
              color: 'white',
              backgroundColor: '#E1000C',

              '&.Mui-disabled': {
                color: '#525252',
                backgroundColor: '#f5f5f5',
              },

              '&:hover': {
                color: 'white',
                backgroundColor: '#E1000C',
                opacity: '.85',
              },
            }}
          >
            ส่ง
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const FormLabel = styled('label')({
  display: 'block',
  fontSize: '1.25rem',
  fontWeight: '500',
  color: '#014B60',
  marginBottom: '10px',
});

export default AddressForm;
